import { useState, KeyboardEvent } from "react";
import { X, Tag as TagIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useTagSuggestions } from "@/hooks/useTagSuggestions";
import { cn } from "@/lib/utils";

interface BookTagInputProps {
  bookId: string;
  userId?: string;
  selectedTags: string[];
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  maxTags?: number;
  disabled?: boolean;
}

export function BookTagInput({
  bookId,
  userId,
  selectedTags,
  onAddTag,
  onRemoveTag,
  maxTags = 10,
  disabled = false,
}: BookTagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { suggestions } = useTagSuggestions(bookId, userId, inputValue);

  const filteredSuggestions = suggestions.filter(
    (s) => !selectedTags.includes(s.tag_name)
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const tag = inputValue.toLowerCase().trim();
      if (!selectedTags.includes(tag) && selectedTags.length < maxTags) {
        onAddTag(tag);
        setInputValue("");
      }
    }
  };

  const handleSelectSuggestion = (tag: string) => {
    if (!selectedTags.includes(tag) && selectedTags.length < maxTags) {
      onAddTag(tag);
      setInputValue("");
      setShowSuggestions(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Selected tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="pl-3 pr-2 py-1.5 text-sm hover:bg-secondary/80 transition-colors"
            >
              {tag}
              <button
                onClick={() => onRemoveTag(tag)}
                disabled={disabled}
                className="ml-1.5 hover:text-destructive transition-colors disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input field */}
      <div className="relative">
        <div className="relative">
          <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            onKeyDown={handleKeyDown}
            placeholder={
              selectedTags.length >= maxTags
                ? `Max ${maxTags} tags reached`
                : "Add tags (e.g., cosy, dark, found family)..."
            }
            disabled={disabled || selectedTags.length >= maxTags}
            className="pl-10"
          />
        </div>

        {/* Tag counter */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {selectedTags.length}/{maxTags}
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && inputValue && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
            <div className="p-2 space-y-1">
              {filteredSuggestions.map((suggestion) => (
                <button
                  key={suggestion.tag_name}
                  onClick={() => handleSelectSuggestion(suggestion.tag_name)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-sm text-sm hover:bg-accent transition-colors",
                    "flex items-center justify-between"
                  )}
                >
                  <span>{suggestion.tag_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {suggestion.source === "preset" && "Preset"}
                    {suggestion.source === "user_history" && "Your tag"}
                    {suggestion.source === "popular" &&
                      `${suggestion.count} readers`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Press Enter to add a tag, or select from suggestions
      </p>
    </div>
  );
}
